import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getNotificationToast } from "../../../store/selectors/general.selector";
import { removeToast } from "../../../store/actions/general.actions";
import { Avatar } from "@fluentui/react-components";
import { utcToLocalDateString } from "../../../utils/systemSettings";
import styles from "./NotificationToast.module.scss";
import {
  DailySummary,
  DeadlineTask,
  UnassignedTask,
} from "../../../modules/default-svg";
import { NotificationType } from "../../../helpers/utils";
import HoverCard from "../../hoverCard/HoverCard";
import { X } from "lucide-react";
import { api as generalService } from '../../../store/services/general.service';
import { REDIRECTION_TYPES } from '../../../helpers/utils';
import { useHistory } from "react-router-dom/cjs/react-router-dom";

const NotificationToastItem = ({ item, onClose, setting, PUBLIC_URL }) => {
  const [isPaused, setIsPaused] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [hasLongContent, setHasLongContent] = useState(false);
  const [displayTitle, setDisplayTitle] = useState("");
  const [displayNotes, setDisplayNotes] = useState("");
  const [isClosing, setIsClosing] = useState(false);
  const timeoutRef = useRef(null);
  const descriptionRef = useRef(null);
  const cardRef = useRef(null);
  const history = useHistory();
  const isHighPriority = item?.priority === 2;

  const clearAutoClose = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const startAutoClose = (force = false) => {
    if (isHighPriority) {
      return;
    }

    const fadedCLose = () => {
      // Start closing animation
      setIsClosing(true);

      // Remove notification after animation completes
      setTimeout(() => {
        onClose(item.id);
      }, 300);
    };

    if (force || !isPaused) {
      clearAutoClose();
      timeoutRef.current = setTimeout(() => {
        fadedCLose();
      }, 6000);
    }
  };

  useEffect(() => {
    startAutoClose();
    return () => clearAutoClose();
  }, []);

  useEffect(() => {
    if (descriptionRef.current) {
      const lineHeight = parseInt(getComputedStyle(descriptionRef.current).lineHeight);
      const height = descriptionRef.current.scrollHeight;
      setHasLongContent(height > lineHeight * 2);
    }
  }, [displayNotes]);


  const transformLinks = (text, links) => {
    if (!text || text.trim() === "" || !text.includes("<@link:"))
      return text;

    const regex = /<@link:(\w+):([a-f0-9\-]+)@>/g;
    const transformedString = text.replace(regex, (match, type, id) => {
      let entityName;
      if (links && links?.length > 0) {
        entityName = links.filter((e) => e.linkId === id).map((e) => e.linkName);
      }
      let linkUrl = "";
      if (type === "Client" || type === "Project" || type === "Scope")
        linkUrl = `${PUBLIC_URL}/${type.toLowerCase()}s/${id}/overview`;
      else if (type === "Portfolio" || type === "Program")
        linkUrl = `${PUBLIC_URL}/${type.toLowerCase()}/${id}/overview`;
      else if (type === "ChangeRequest" || type === "CommunicationPlan" || type === "Budget" || type === "Risk" || type === "Task")
        return `<a target="_blank" class="notification-redirection-link cursor-pointer" data-type="${type.toLowerCase()}" data-id="${id}">${entityName}</a>`;

      if (linkUrl !== "")
        return `<a href=${linkUrl} target="_self" data-type="${type}" data-id="${id}">${entityName}</a>`;
      else
        return `<span class="text-primary">${entityName}</span>`;
    });
    return transformedString;
  }

  useEffect(() => {
    const handleLinkClick = (e) => {
      const target = e.target;
      // Check if the clicked element has the redirection class
      if (target.classList.contains('notification-redirection-link')) {
        e.preventDefault();
        e.stopPropagation();

        const type = target.getAttribute('data-type');
        const id = target.getAttribute('data-id');

        if (type && id) {
          generalService.getNotificationLinkRedirectionRequest({ type, id })
            .then((response) => {
              if (response) {
                if (response.scopeId)
                  handleScopeRedirection(response.scopeId, id);

                else if (response.projectId)
                  handleProjectRedirection(response.projectId, type, id);

              }
            }).catch((error) => {
            });
        }
      }
    };

    const cardElement = cardRef.current;
    if (cardElement) {
      cardElement.addEventListener('click', handleLinkClick);
    }

    return () => {
      if (cardElement) {
        cardElement.removeEventListener('click', handleLinkClick);
      }
    };
  }, []);
  const handleScopeRedirection = (scopeId, taskId) => {
    history.push(`/scopes/${scopeId}/overview?r_id=${taskId}`);
  };

  const handleProjectRedirection = (projectId, type, id) => {
    if (projectId && type && id) {
      let redirectionType;
      switch (type) {
        case 'changerequest':
          redirectionType = REDIRECTION_TYPES.CHANGE_REQUEST;
          break;
        case 'communicationplan':
          redirectionType = REDIRECTION_TYPES.COMMUNICATION;
          break;
        case 'budget':
          redirectionType = REDIRECTION_TYPES.BUDGET;
          break;
        case 'risk':
          redirectionType = REDIRECTION_TYPES.RISK;
          break;
        case 'task':
          redirectionType = REDIRECTION_TYPES.TASK;
          break;
        default:
          return;
      }

      history.push(`/projects/${projectId}/overview?r_t=${redirectionType}&r_id=${id}`);
    }

  };


  useEffect(() => {
    setDisplayTitle(transformLinks(item?.title, item?.links));
    setDisplayNotes(transformLinks(item?.notes, item?.links));
  }, [item?.title, item?.notes]);

  const handleMouseEnter = () => {
    setIsPaused(true);
    clearAutoClose();
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
    startAutoClose(true);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    clearAutoClose();

    // Start closing animation
    setIsClosing(true);

    // Remove notification after animation completes
    setTimeout(() => {
      onClose(item.id);
    }, 300);
  };

  const handleExpand = (e) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  const getNotificationIcon = () => {
    switch (item.notificationType) {
      case "unassignedTask":
        return <UnassignedTask />;
      case "taskDeadline":
        return <DeadlineTask />;
      case "dailySummary":
        return <DailySummary />;
      default:
        return <UnassignedTask />;
    }
  };

  const getIconColor = () => {
    switch (item.notificationType) {
      case "unassignedTask":
        return "blue";
      case "taskDeadline":
        return "red";
      case "dailySummary":
        return "beige";
      default:
        return "blue";
    }
  };

  const getNotificationTypeClass = () => {
    switch (item.notificationType) {
      case "unassignedTask":
        return styles.notificationTypeUnassigned;
      case "taskDeadline":
        return styles.notificationTypeDeadline;
      case "dailySummary":
        return styles.notificationTypeSummary;
      default:
        return "";
    }
  };

  const getPriorityClass = () => {
    const priority = item?.priority || 0;
    switch (priority) {
      case 2:
        return styles.priorityHigh;
      case 1:
        return styles.priorityModerate;
      default:
        return styles.priorityLow;
    }
  };

  return (
    <div
      className={`${styles.notificationContainer} ${getPriorityClass()} ${isClosing ? styles.closing : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        ref={cardRef} 
        className={`${styles.remindCardWrapper} ${getNotificationTypeClass()} ${expanded ? styles.expanded : ''}`}>
        <button className={styles.closeButton} onClick={handleClose}>
          <X size={16} />
        </button>

        <div className={styles.avatarWrapper}>
          {item?.creator ? (
            <HoverCard
              HoverCardTrigger={
                <div>
                  <Avatar
                    size={28}
                    color="colorful"
                    name={item?.creator?.name}
                    image={{ src: item?.creator?.image || null }}
                  />
                </div>
              }
              AvatarId={item?.creator?.id}
              AvatarName={item?.creator?.name}
              AvatarImage={item?.creator?.image}
              AvatarDesignation={item?.creator?.title}
              AvatarEmail={item?.creator?.email}
              AvatarPhone={item?.creator?.phoneNumber}
              AvatarIsExternal={item?.creator?.isExternal}
              CanSendMessage={true}
            />
          ) : (
            <Avatar
              size={28}
              color={getIconColor()}
              icon={getNotificationIcon()}
            />
          )}
        </div>

        <div className={styles.remindCardContent}>
          <div className={styles.titleWrapper}>
            {displayTitle && (
              <p className={styles.notificationTitle}>
                <span dangerouslySetInnerHTML={{ __html: displayTitle }} />
              </p>
            )}
          </div>

          {displayNotes && (
            <div className={styles.remindDescription} onClick={handleExpand}>
              <p ref={descriptionRef} dangerouslySetInnerHTML={{ __html: displayNotes }} />
              {hasLongContent && (
                <button className={styles.readMoreButton} onClick={handleExpand}>
                  {expanded ? (
                    <>
                      Show less
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="18 15 12 9 6 15"></polyline>
                      </svg>
                    </>
                  ) : (
                    <>
                      Read more
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const NotificationToast = () => {
  const [notifications, setNotifications] = useState([]);
  const dispatch = useDispatch();
  const PUBLIC_URL = process.env.PUBLIC_URL;
  const incomingNotification = useSelector(getNotificationToast);
  const settings = JSON.parse(localStorage.getItem("settings"));
  const [setting] = useState(settings);

  useEffect(() => {
    if (incomingNotification && incomingNotification.hasOwnProperty("title")) {
      const newNotification = {
        ...incomingNotification,
        id: `${Date.now()}-${Math.random()}`,
      };
      setNotifications((prev) => [...prev, newNotification]);
    }
  }, [incomingNotification]);

  const handleClose = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    dispatch(removeToast());
  };

  return (
    <div className={styles.toastStack}>
      {notifications.map((notification) => (
        <NotificationToastItem
          key={notification.id}
          item={notification}
          onClose={handleClose}
          setting={setting}
          PUBLIC_URL={PUBLIC_URL}
        />
      ))}
    </div>
  );
};

export default NotificationToast;